import React, { useState } from 'react';
import QRCode from 'qrcode';
import { adminApi } from '../../services/adminApi';

interface MobileEnrollmentPanelProps {
  tenantSlug: string;
  tenantName: string;
}

export const MobileEnrollmentPanel: React.FC<MobileEnrollmentPanelProps> = ({ tenantSlug, tenantName }) => {
  const [apiBaseUrl, setApiBaseUrl] = useState<string>((import.meta.env.VITE_API_BASE_URL || '').replace(/\/$/, ''));
  const [envLabel, setEnvLabel] = useState<string>('prod-au');
  const [expiresInSeconds, setExpiresInSeconds] = useState<string>('600');
  const [payloadJson, setPayloadJson] = useState<string>('');
  const [qrDataUrl, setQrDataUrl] = useState<string>('');
  const [status, setStatus] = useState<string>('');
  const [busy, setBusy] = useState<boolean>(false);

  const handleGenerate = async () => {
    setBusy(true);
    setStatus('');
    try {
      const response = await adminApi.generateMobileEnrollmentQr({
        apiBaseUrl: apiBaseUrl.trim(),
        tenantSlug,
        tenantName,
        env: envLabel.trim() || undefined,
        expiresInSeconds: Number(expiresInSeconds) || 600,
      });

      const payload = response.payloadJson || '';
      setPayloadJson(payload);
      setQrDataUrl(await QRCode.toDataURL(payload, { errorCorrectionLevel: 'L', margin: 2, width: 420 }));
      setStatus(`QR generated for tenant '${tenantSlug}'`);
    } catch (error) {
      setStatus(error instanceof Error ? error.message : 'Failed to generate mobile enrollment QR');
    } finally {
      setBusy(false);
    }
  };

  const handleCopyPayload = async () => {
    if (!payloadJson) {
      setStatus('No payload available to copy');
      return;
    }
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(payloadJson);
        setStatus('Payload copied to clipboard');
        return;
      }
      setStatus('Clipboard API unavailable');
    } catch (error) {
      setStatus(error instanceof Error ? error.message : 'Copy failed');
    }
  };

  return (
    <div className="mobile-enrollment-page">
      <div className="mobile-enrollment-hero">
        <h3 className="mobile-enrollment-title">Mobile Enrollment</h3>
        <p className="mobile-enrollment-subtitle">
          Generate a signed onboarding payload for tenant-scoped mobile enrollment.
          Users can scan the QR code or paste payload JSON during first-run setup.
        </p>
      </div>

      <div className="mobile-enrollment-card">
        <div className="form-row">
          <div className="form-group">
            <label>Tenant slug</label>
            <input type="text" value={tenantSlug} readOnly />
          </div>
          <div className="form-group">
            <label>Tenant name</label>
            <input type="text" value={tenantName || tenantSlug} readOnly />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>API base URL</label>
            <input
              type="text"
              value={apiBaseUrl}
              onChange={(e) => setApiBaseUrl(e.target.value)}
              placeholder="https://zurvit-func-01.azurewebsites.net"
            />
          </div>
          <div className="form-group">
            <label>Environment label</label>
            <input
              type="text"
              value={envLabel}
              onChange={(e) => setEnvLabel(e.target.value)}
              placeholder="prod-au"
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group mobile-enrollment-expiry">
            <label>Expiry seconds</label>
            <input
              type="number"
              min={60}
              max={3600}
              value={expiresInSeconds}
              onChange={(e) => setExpiresInSeconds(e.target.value)}
            />
          </div>
        </div>

        <div className="form-actions mobile-enrollment-actions">
          <button className="btn-primary" onClick={handleGenerate} disabled={busy}>
            {busy ? 'Generating...' : 'Generate tenant QR'}
          </button>
          <button className="btn-secondary" onClick={() => void handleCopyPayload()} disabled={!payloadJson}>
            Copy payload JSON
          </button>
        </div>

        {status ? <p className="table-info mobile-enrollment-status">{status}</p> : null}

        <div className="mobile-enrollment-results">
          <div className="form-group">
            <label>Tenant enrollment QR</label>
            <div className="mobile-enrollment-qr-wrap">
              {qrDataUrl ? <img src={qrDataUrl} alt="Tenant enrollment QR" className="mobile-enrollment-qr" /> : <span className="table-info">Generate payload to render QR</span>}
            </div>
          </div>
          <div className="form-group">
            <label>Payload JSON</label>
            <textarea value={payloadJson} onChange={() => undefined} rows={10} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default MobileEnrollmentPanel;
